import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectApi } from '../lib/api';
import {
  formatDate, formatCurrency, formatPercentage,
  projectStatusLabels, projectStatusColors, getBadgeClass
} from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Building2, Search, Filter, Clock, AlertTriangle, Euro, MapPin,
  User, HardHat, CalendarDays, MoreVertical, FileText, ExternalLink, ArrowUpDown
} from 'lucide-react';

// Stati con cantiere realmente avviato → label finanziaria "Prossimo incasso"
const CANTIERE_STARTED = ['cantiere_avviato', 'in_corso', 'sospeso', 'completato', 'chiuso'];
// Ordine di workflow canonico (chiavi in ordine lifecycle), usato per l'ordinamento per Stato
const STATUS_ORDER = Object.keys(projectStatusLabels);

// Label semantica del prossimo step finanziario (fonte: project_milestones canonico via next_milestone)
const nextStepLabel = (project) => {
  const nm = project.next_milestone;
  if (!nm) return null;
  if (nm.status === 'fatturato') return 'Da incassare';
  if (CANTIERE_STARTED.includes(project.status)) return 'Prossimo incasso';
  return 'Prossimo step';
};

const ProjectsPage = () => {
  const { isAdmin, isAdministration, isProjectManager } = useAuth();
  const canViewEconomic = isAdmin || isAdministration || isProjectManager;
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('default');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectApi.getAll();
      setProjects(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei progetti');
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(project => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      project.effective_site_address?.toLowerCase().includes(searchLower) ||
      project.project_number?.toLowerCase().includes(searchLower) ||
      project.client_name?.toLowerCase().includes(searchLower) ||
      project.property_address?.toLowerCase().includes(searchLower) ||
      project.client_address?.toLowerCase().includes(searchLower) ||
      project.contract_number?.toLowerCase().includes(searchLower) ||
      project.client_email?.toLowerCase().includes(searchLower) ||
      project.client_phone?.toLowerCase().includes(searchLower);

    let matchesStatus = true;
    if (statusFilter === 'attivi') {
      matchesStatus = ['cantiere_avviato', 'in_corso'].includes(project.status);
    } else if (statusFilter === 'in_attesa') {
      matchesStatus = ['in_attesa_acconto', 'acconto_da_fatturare'].includes(project.status);
    } else if (statusFilter !== 'all') {
      matchesStatus = project.status === statusFilter;
    }

    return matchesSearch && matchesStatus;
  });

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    switch (sortBy) {
      case 'status': {
        const ia = STATUS_ORDER.indexOf(a.status);
        const ib = STATUS_ORDER.indexOf(b.status);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      }
      case 'sal':
        return (b.sal_percentage || 0) - (a.sal_percentage || 0);
      case 'start':
        return new Date(b.start_date || 0) - new Date(a.start_date || 0);
      case 'value':
        return (b.total_amount || 0) - (a.total_amount || 0);
      case 'next':
        return (b.next_milestone?.amount || 0) - (a.next_milestone?.amount || 0);
      default:
        return 0;
    }
  });

  const toggleFilter = (value) => setStatusFilter(statusFilter === value ? 'all' : value);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h2">Progetti</h1>
          <p className="text-body-small mt-1">Gestisci cantieri e avanzamento lavori</p>
        </div>
      </div>

      {/* KPI compatti — primi 3 = quick filter (riuso sistema filtri), 4° = metrica pura */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <button
          type="button"
          className={`text-left rounded-xl border bg-white px-3 py-2.5 transition-all duration-200 hover:shadow-md ${
            statusFilter === 'attivi' ? 'border-[#008A3D] ring-1 ring-[#008A3D]' : 'border-[#E4E4E4]'
          }`}
          onClick={() => toggleFilter('attivi')}
          data-testid="filter-active"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-[#E5F7ED] rounded-lg flex items-center justify-center shrink-0">
              <Building2 className="w-4 h-4 text-[#008A3D]" />
            </div>
            <div className="min-w-0">
              <p className="text-xl font-semibold text-[#3C3C3C] leading-none">
                {projects.filter(p => ['cantiere_avviato', 'in_corso'].includes(p.status)).length}
              </p>
              <p className="text-[11px] text-[#737373] mt-1 truncate">Cantieri attivi</p>
            </div>
          </div>
        </button>

        <button
          type="button"
          className={`text-left rounded-xl border bg-white px-3 py-2.5 transition-all duration-200 hover:shadow-md ${
            statusFilter === 'in_attesa' ? 'border-[#FF7819] ring-1 ring-[#FF7819]' : 'border-[#E4E4E4]'
          }`}
          onClick={() => toggleFilter('in_attesa')}
          data-testid="filter-waiting"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-[#FFF0E5] rounded-lg flex items-center justify-center shrink-0">
              <Clock className="w-4 h-4 text-[#FF7819]" />
            </div>
            <div className="min-w-0">
              <p className="text-xl font-semibold text-[#3C3C3C] leading-none">
                {projects.filter(p => ['in_attesa_acconto', 'acconto_da_fatturare'].includes(p.status)).length}
              </p>
              <p className="text-[11px] text-[#737373] mt-1 truncate">In attesa</p>
            </div>
          </div>
        </button>

        <button
          type="button"
          className={`text-left rounded-xl border bg-white px-3 py-2.5 transition-all duration-200 hover:shadow-md ${
            statusFilter === 'sospeso' ? 'border-[#CC0000] ring-1 ring-[#CC0000]' : 'border-[#E4E4E4]'
          }`}
          onClick={() => toggleFilter('sospeso')}
          data-testid="filter-suspended"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-[#FFEDED] rounded-lg flex items-center justify-center shrink-0">
              <AlertTriangle className="w-4 h-4 text-[#CC0000]" />
            </div>
            <div className="min-w-0">
              <p className="text-xl font-semibold text-[#3C3C3C] leading-none">
                {projects.filter(p => p.status === 'sospeso').length}
              </p>
              <p className="text-[11px] text-[#737373] mt-1 truncate">Sospesi</p>
            </div>
          </div>
        </button>

        {/* Metrica pura: nessuno stato selezionato, nessun bordo blu di "selezione" */}
        <div className="rounded-xl border border-[#E4E4E4] bg-white px-3 py-2.5" data-testid="kpi-total-value">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-[#E5F4FF] rounded-lg flex items-center justify-center shrink-0">
              <Euro className="w-4 h-4 text-[#0078C8]" />
            </div>
            <div className="min-w-0">
              <p className="text-base sm:text-xl font-semibold text-[#3C3C3C] leading-none truncate">
                {canViewEconomic
                  ? formatCurrency(projects.reduce((sum, p) => sum + (p.total_amount || 0), 0))
                  : projects.length}
              </p>
              <p className="text-[11px] text-[#737373] mt-1 truncate">{canViewEconomic ? 'Valore totale' : 'Tutti i progetti'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar: search + filtro stato + ordinamento */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
          <Input
            placeholder="Cerca per cliente, indirizzo, codice progetto, contratto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-abitalo pl-10"
            data-testid="projects-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-44" data-testid="projects-status-filter">
            <Filter className="w-4 h-4 mr-2 text-[#737373]" />
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i progetti</SelectItem>
            <SelectItem value="attivi">Cantieri attivi</SelectItem>
            <SelectItem value="in_attesa">In attesa</SelectItem>
            {Object.entries(projectStatusLabels).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full md:w-52" data-testid="projects-sort">
            <ArrowUpDown className="w-4 h-4 mr-2 text-[#737373]" />
            <SelectValue placeholder="Ordina" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Più recenti</SelectItem>
            <SelectItem value="status">Stato</SelectItem>
            <SelectItem value="sal">Avanzamento SAL</SelectItem>
            <SelectItem value="start">Data inizio</SelectItem>
            {canViewEconomic && <SelectItem value="value">Valore commessa</SelectItem>}
            {canViewEconomic && <SelectItem value="next">Prossimo incasso</SelectItem>}
          </SelectContent>
        </Select>
      </div>

      {/* Grid progetti — 2 card per riga su desktop (xl), 1 su tablet/mobile */}
      {sortedProjects.length === 0 ? (
        <Card className="card-abitalo">
          <CardContent className="p-8 text-center">
            <p className="text-[#737373]">Nessun progetto trovato</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {sortedProjects.map((project) => {
            const nm = project.next_milestone;
            const nmLabel = nextStepLabel(project);
            return (
              <div
                key={project.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/projects/${project.id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/projects/${project.id}`); }}
                className="group rounded-2xl border border-[#E4E4E4] bg-white p-4 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-[#D4D4D4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF7819]"
                data-testid={`project-card-${project.id}`}
              >
                {/* HEADER */}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-[#171717] truncate">
                      {project.client_name || 'Cliente non specificato'}
                    </h3>
                    {(project.effective_site_address || project.site_address_unavailable) && (
                      <div className="flex items-center gap-1 mt-0.5 text-sm text-[#737373]">
                        <MapPin className="w-3.5 h-3.5 shrink-0" />
                        <span className={`truncate ${project.site_address_unavailable ? 'text-amber-600 italic' : ''}`} data-testid={`project-site-address-${project.id}`}>
                          {project.site_address_unavailable ? 'Indirizzo cantiere non disponibile' : project.effective_site_address}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1.5 text-xs">
                      <span className="text-[#525252] font-medium whitespace-nowrap">{project.project_number}</span>
                      {project.contract_number && (
                        <>
                          <span className="text-[#D4D4D4]">·</span>
                          <span className="text-[#0078C8] whitespace-nowrap">{project.contract_number}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className={getBadgeClass(projectStatusColors[project.status])}>
                      {projectStatusLabels[project.status]}
                    </span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          onClick={(e) => e.stopPropagation()}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-[#737373] hover:bg-[#F5F5F5] transition-colors"
                          data-testid={`project-menu-${project.id}`}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.id}`); }}>
                          <ExternalLink className="w-4 h-4 mr-2" /> Apri progetto
                        </DropdownMenuItem>
                        {project.contract_id && (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/contracts/${project.contract_id}`); }}>
                            <FileText className="w-4 h-4 mr-2" /> Apri contratto
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                <div className="my-3 border-t border-[#F0F0F0]" />

                {/* AREA ECONOMICA + OPERATIVA */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-[#A3A3A3] font-medium">Valore commessa</p>
                    {canViewEconomic ? (
                      <>
                        <p className="text-lg font-bold text-[#171717] mt-0.5 leading-tight">{formatCurrency(project.total_amount)}</p>
                        <div className="mt-1 space-y-0.5 text-xs text-[#737373]">
                          <p>Contratto <span className="text-[#525252]">{formatCurrency(project.contract_amount)}</span></p>
                          <p>Extra <span className="text-[#525252]">{formatCurrency(project.approved_extras_total || 0)}</span></p>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-[#A3A3A3] mt-1">—</p>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] uppercase tracking-wide text-[#A3A3A3] font-medium">Avanzamento SAL</p>
                      <span className="text-sm font-semibold text-[#FF7819]">{formatPercentage(project.sal_percentage || 0)}</span>
                    </div>
                    <Progress value={project.sal_percentage || 0} className="h-2 mt-2" />
                  </div>
                </div>

                {/* AREA FINANZIARIA */}
                {canViewEconomic && (
                  <div className="grid grid-cols-2 gap-3 mt-3 items-stretch">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-[#A3A3A3] font-medium">Incassato</p>
                      <p className="text-base font-semibold text-[#008A3D] mt-0.5" data-testid={`project-incassato-${project.id}`}>
                        {formatCurrency(project.incassato_total || 0)}
                      </p>
                    </div>
                    <div className="rounded-lg bg-[#FFFBF0] border border-[#FBE8C8] px-2.5 py-1.5">
                      <p className="text-[11px] uppercase tracking-wide text-[#B38C00] font-medium">{nmLabel || 'Prossimo incasso'}</p>
                      {nm ? (
                        <div className="mt-0.5" data-testid={`project-next-${project.id}`}>
                          <p className="text-sm font-semibold text-[#946F00] truncate">{nm.name}</p>
                          <p className="text-xs text-[#9A7A1A]">{formatCurrency(nm.amount)}</p>
                        </div>
                      ) : (
                        <p className="text-sm text-[#B38C00]/60 mt-0.5">—</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="my-3 border-t border-[#F0F0F0]" />

                {/* FOOTER */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[#525252]">
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarDays className="w-4 h-4 text-[#A3A3A3]" /> {formatDate(project.start_date)}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <User className="w-4 h-4 text-[#A3A3A3]" /> <span className="text-[#A3A3A3]">PM</span> {project.project_manager_name || '—'}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <HardHat className="w-4 h-4 text-[#A3A3A3]" /> <span className="text-[#A3A3A3]">DL</span> {project.director_of_works_name_snapshot || '—'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
